import WhatsApp from '@mui/icons-material/WhatsApp'
import { Box } from '@mui/material'
import Button from '@mui/material/Button'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import React from 'react'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import { Select, SelectMultiple } from '@/app/component/atom/form'
import Loading from '@/app/component/guard/loading'
import { DEFAULT_SENDER_ID_MESSAGE, DEFAULT_SENDER_ID_VALUE, noop } from '@/app/definitions'
import { ContentWhatsApp } from '@/app/module/campaigns/types'
import WhatsappMessage from '@/app/module/common/whatsapp'
import { shouldDisplayConfirmation, WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { SendWhatsappMessagePayload } from '@/app/module/logs/types'
import { formatPhoneNumber } from '@/app/service/util'
import { FileType, FileUploadType, PersonalizationType } from '@/app/types'

import { useGetWhatsappSenderOptions } from '../hooks/use-get-whatsapp-sender-options'

type Props = {
  contactIds: number[]
  excludeIds: number[]
  includedGroupIds: number[]
  isAllContacts: boolean
  isOpen: boolean
  loading: boolean
  personalizationList: PersonalizationType
  recipients: { label: string; value: string }[]
  search: string
  senderId: string
  totalRecipients: number

  closeHandler: () => void
  getFile: (item: string) => Promise<FileType>
  onMicAccessError: () => void
  saveFile: (file: FileUploadType) => Promise<FileType>
  sendWhatsappMessage: (args: SendWhatsappMessagePayload) => Promise<unknown>
}

export default function SendWhatsappDialog(props: Props) {
  const {
    contactIds,
    excludeIds,
    includedGroupIds,
    isAllContacts,
    isOpen,
    loading = false,
    personalizationList,
    recipients,
    search,
    senderId: senderIdProp,
    totalRecipients,

    closeHandler,
    getFile,
    onMicAccessError,
    saveFile,
    sendWhatsappMessage,
  } = props

  const { senderIds } = useGetWhatsappSenderOptions()

  const [senderId, setSenderId] = React.useState(senderIdProp || '')
  const [message, setMessage] = React.useState('')
  const [image, setImage] = React.useState('')
  const [audio, setAudio] = React.useState('')
  const [video, setVideo] = React.useState('')
  const [document, setDocument] = React.useState('')
  const [playId, setPlaying] = React.useState('')
  const [files, setFiles] = React.useState({})

  React.useEffect(() => {
    const nextSenderId = senderIdProp || ''
    const senderIdExists = senderIds.some((sender) => sender.value === nextSenderId)
    setSenderId(senderIdExists ? nextSenderId : DEFAULT_SENDER_ID_VALUE)
  }, [senderIdProp, senderIds])

  const sendHandler = () => {
    sendWhatsappMessage({
      message,
      image,
      video,
      document,
      audio,
      senderId: senderId === DEFAULT_SENDER_ID_VALUE ? '' : senderId,
      isAllContacts,
      contactIds,
      excludeIds,
      includedGroupIds,
      search,
    })
      .then(() => {
        closeHandler()
      })
      .catch(() => {
        closeHandler()
      })
  }

  const saveFileHandler = (file: FileUploadType) =>
    saveFile(file).then((res: FileType) => {
      if (file.cancelled) {
        return
      }
      if (!(res instanceof Error)) {
        if (file.type === 'image') {
          setImage(res.filename)
        } else if (file.type === 'video') {
          setVideo(res.filename)
        } else if (file.type === 'document') {
          setDocument(res.filename)
        }
      }
    })

  const saveAudioHandler = (file: FileUploadType) =>
    saveFile(file).then((res: FileType) => {
      if (!(res instanceof Error)) {
        setAudio(res.filename)
      }
      return res
    })

  const removeMedia = () => {
    setImage('')
    setAudio('')
    setVideo('')
    setDocument('')
  }

  const recipientsDisplay = recipients.slice(0, 4).map(({ label, value }: { label: string; value: string }) => ({
    label: formatPhoneNumber(`+${label}`),
    value,
  }))
  if (totalRecipients > 4) {
    recipientsDisplay.push({
      label: '...',
      value: 'more',
    })
  }
  const handleGetFile = React.useCallback(
    (item: string) =>
      getFile(item).then((file: FileType) => {
        setFiles({ [item]: file })
        return file
      }),
    [getFile],
  )
  const handleWhatsAppChange = React.useCallback(
    ({ text: updatedText }: ContentWhatsApp) => setMessage(updatedText || ''),
    [],
  )
  const shouldConfirm = (tab: WhatsAppTab, content: ContentWhatsApp) => shouldDisplayConfirmation(tab, content, false) // Send WA message in Logs doesn't support button or list (yet)

  return (
    <Dialog open={isOpen} onClose={closeHandler} fullWidth maxWidth="md">
      <DialogHeader icon={<WhatsApp />} title="Send WhatsApp message" onClose={closeHandler} />
      <Loading isLoading={loading}>
        <form
          id="send-message-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (
              !loading &&
              (!!message || !!audio || !!image || !!video || !!document) &&
              senderId &&
              recipients.length
            ) {
              sendHandler()
            }
          }}
        >
          <CardContent>
            <WhatsappMessage
              audio={audio}
              canRegisterTemplate={false}
              document={document}
              defaultLanguage=""
              enableMediaCaption
              files={files}
              image={image}
              info=""
              label="message"
              loading={loading}
              personalizationList={personalizationList}
              playId={playId}
              text={message}
              video={video}
              changeHandler={handleWhatsAppChange}
              getFile={handleGetFile}
              onMicAccessError={onMicAccessError}
              onRecordingChange={noop}
              removeMedia={removeMedia}
              saveFileHandler={saveAudioHandler}
              saveHandler={saveFileHandler}
              setPlaying={setPlaying}
              shouldConfirm={shouldConfirm}
            />
            <br />
            <SelectMultiple
              label="To"
              name="recipients"
              value={recipientsDisplay.map(({ value }) => value)}
              values={recipientsDisplay}
              editable={false}
              message={
                totalRecipients !== 1
                  ? `This message will be sent to ${totalRecipients} contacts`
                  : `This message will be sent to ${totalRecipients} contact`
              }
            />
            <Select
              name="senderid"
              id="send-message-sender"
              label="From"
              value={senderId}
              values={senderIds}
              editable={true}
              onChange={({ value }: any) => {
                setSenderId(value)
              }}
              info={
                senderId === DEFAULT_SENDER_ID_VALUE && (senderIds as any).length === 1 ? DEFAULT_SENDER_ID_MESSAGE : ''
              }
            />
          </CardContent>
          <CardActions>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                id="send-message-action"
                type="submit"
                variant="contained"
                color="primary"
                disabled={
                  loading ||
                  (!message && !audio && !image && !video && !document) ||
                  !senderId ||
                  senderId === '0' ||
                  !recipients.length
                }
              >
                Send
              </Button>
            </Box>
          </CardActions>
        </form>
      </Loading>
    </Dialog>
  )
}
